import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Building2, ExternalLink, Pencil } from "lucide-react";
import { useTranslation } from "@/shared/providers/LanguageProvider";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { StatusBadge } from "../components/StatusBadge";
import { ApiError, sanitizeApiErrorMessage } from "@/shared/lib/apiClient";
import { fetchAdminConvention, type AdminConventionDetail } from "../api/adminApi";
import { getAuthToken } from "../auth";
import { cn } from "@/shared/lib/cn";

function formatMoney(value: number | null | undefined) {
  if (value == null) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(value);
}

function statusVariant(status: string) {
  switch (status) {
    case "active":
      return "success" as const;
    case "expiring":
      return "warning" as const;
    case "expired":
    case "cancelled":
      return "danger" as const;
    case "suspended":
      return "neutral" as const;
    default:
      return "neutral" as const;
  }
}

function progressBarClass(pct: number, threshold: number) {
  if (pct >= 100) return "bg-pink";
  if (pct >= threshold) return "bg-yellow";
  return "bg-primary";
}

function DetailRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid grid-cols-1 gap-1 border-b border-border py-3 last:border-0 sm:grid-cols-3">
      <dt className="text-xs font-semibold uppercase tracking-wide text-muted">{label}</dt>
      <dd className="text-sm text-foreground sm:col-span-2">{children}</dd>
    </div>
  );
}

function SideRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-3 border-b border-border py-2 last:border-0">
      <dt className="text-xs text-muted">{label}</dt>
      <dd className="text-right text-sm font-medium text-foreground">{value}</dd>
    </div>
  );
}

export function ViewConventionPage() {
  const { t } = useTranslation();
  const labels = t.safecheck.conventions;
  const view = labels.view;
  const formFields = labels.create.fields;
  const { id } = useParams();
  const conventionId = Number(id);
  const [convention, setConvention] = useState<AdminConventionDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!getAuthToken() || !conventionId) {
      setError(labels.empty);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    void fetchAdminConvention(conventionId)
      .then((row) => {
        if (!cancelled) setConvention(row);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 401) {
          setError(null);
          return;
        }
        setError(
          sanitizeApiErrorMessage(
            err instanceof Error ? err.message : "Impossible de charger la convention.",
          ),
        );
        setConvention(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [conventionId, labels.empty]);

  const isForfait = convention?.type === "forfaitaire";
  const consumption = convention?.consumption ?? null;
  const budget = consumption?.budget ?? convention?.budget ?? 0;
  const consumptionPct =
    consumption?.consumption_pct ?? convention?.consumption_pct ?? 0;
  const consumed =
    consumption?.consumed ??
    (budget > 0 ? (budget * consumptionPct) / 100 : 0);
  const remaining =
    consumption?.remaining ?? Math.max(budget - consumed, 0);
  const threshold = convention?.global_budget_alert_threshold ?? 80;
  const consumptionInvoices = consumption?.invoices ?? [];
  const invoicesTotal = useMemo(
    () => consumptionInvoices.reduce((sum, row) => sum + row.amount_usd, 0),
    [consumptionInvoices],
  );

  const providerForfaits = useMemo(
    () =>
      (convention?.providers ?? [])
        .map((p) => p.forfait)
        .filter((v): v is number => v != null && v > 0),
    [convention?.providers],
  );

  const coveredTypes = useMemo(() => {
    if (!convention) return [];
    const types: string[] = [];
    if (convention.covers_spouse) types.push(labels.create.dependentTypes.spouse);
    if (convention.covers_children) types.push(labels.create.dependentTypes.children);
    if (convention.covers_parents) types.push(labels.create.dependentTypes.parents);
    if (convention.covers_other) types.push(labels.create.dependentTypes.other);
    return types;
  }, [convention, labels.create.dependentTypes]);

  if (loading) {
    return <div className="theme-card p-8 text-center text-sm text-muted">Chargement…</div>;
  }

  if (!convention) {
    return (
      <div className="theme-card p-8 text-center">
        <p className="text-sm text-muted">{error ?? labels.empty}</p>
        <Link
          to="/dashboard/conventions"
          className="mt-4 inline-flex items-center gap-2 text-sm text-primary"
        >
          <ArrowLeft size={14} />
          {labels.create.backToList}
        </Link>
      </div>
    );
  }

  return (
    <>
      <Breadcrumbs
        items={[
          { label: labels.title, href: "/dashboard/conventions" },
          { label: convention.name || convention.code },
        ]}
      />

      <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-foreground">{convention.name}</h1>
          <p className="mt-1 text-sm text-muted">
            {convention.code} · {convention.company}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <StatusBadge variant={statusVariant(convention.status)}>
            {labels.statuses[convention.status] ?? convention.status}
          </StatusBadge>
          <Link
            to={`/dashboard/conventions/${convention.id}/edit`}
            className="inline-flex items-center gap-2 rounded-lg bg-primary px-3 py-2 text-sm font-medium text-white hover:bg-primary/90"
          >
            <Pencil size={14} />
            {view.edit}
          </Link>
          <Link
            to="/dashboard/conventions"
            className="inline-flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm text-muted-foreground hover:bg-card-muted"
          >
            <ArrowLeft size={14} />
            {view.backToList}
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-5 xl:grid-cols-3">
        <div className="space-y-5 xl:col-span-2">
          <section className="theme-card overflow-hidden">
            <div className="border-b border-border px-5 py-3">
              <h2 className="text-sm font-semibold text-foreground">{view.generalInfo}</h2>
            </div>
            <dl className="px-5 py-1">
              <DetailRow label={view.code}>
                <code className="rounded bg-card-muted px-2 py-0.5 text-sm">{convention.code}</code>
              </DetailRow>
              <DetailRow label={view.company}>{convention.company}</DetailRow>
              <DetailRow label={view.providers}>
                {convention.providers.length > 0 ? (
                  <ul className="space-y-2">
                    {convention.providers.map((provider) => (
                      <li key={provider.id} className="flex flex-wrap items-center gap-2">
                        <Building2 size={14} className="text-primary" />
                        <span>{provider.commercial_name}</span>
                        <span className="rounded-md bg-card-muted px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-muted">
                          {provider.type.replace(/_/g, " ")}
                        </span>
                        {provider.forfait != null && provider.forfait > 0 ? (
                          <span className="rounded-md bg-yellow-soft px-2 py-0.5 text-[11px] font-semibold text-yellow">
                            {formatMoney(provider.forfait)}
                            {view.perFamily}
                          </span>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <span className="text-muted">{view.noProviders}</span>
                )}
              </DetailRow>
              <DetailRow label={view.treatmentType}>
                <span className="inline-flex rounded-md bg-primary-soft px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-primary">
                  {labels.types[convention.type as keyof typeof labels.types] ?? convention.type}
                </span>
              </DetailRow>
              <DetailRow label={view.validity}>
                {convention.validity_start} {labels.validitySeparator} {convention.validity_end}
              </DetailRow>
              {convention.description ? (
                <DetailRow label={view.description}>
                  <p className="whitespace-pre-wrap">{convention.description}</p>
                </DetailRow>
              ) : null}
              {convention.special_conditions ? (
                <DetailRow label={view.specialConditions}>
                  <p className="whitespace-pre-wrap">{convention.special_conditions}</p>
                </DetailRow>
              ) : null}
            </dl>
          </section>

          <section className="theme-card overflow-hidden">
            <div className="border-b border-border px-5 py-3">
              <h2 className="text-sm font-semibold text-foreground">{view.budgetConsumption}</h2>
            </div>
            <div className="p-5">
              <div className="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-3">
                <div className="rounded-lg bg-primary-soft p-4 text-center">
                  <p className="text-lg font-bold text-foreground">{formatMoney(budget)}</p>
                  <p className="mt-1 text-xs text-muted">
                    {isForfait ? view.monthlyForfait : view.budgetTotal}
                  </p>
                </div>
                <div
                  className={cn(
                    "rounded-lg p-4 text-center",
                    consumptionPct >= 100
                      ? "bg-pink-soft"
                      : consumptionPct >= threshold
                        ? "bg-yellow-soft"
                        : "bg-teal-soft",
                  )}
                >
                  <p className="text-lg font-bold text-foreground">{formatMoney(consumed)}</p>
                  <p className="mt-1 text-xs text-muted">{view.consumed}</p>
                </div>
                <div className="rounded-lg bg-teal-soft p-4 text-center">
                  <p className="text-lg font-bold text-foreground">{formatMoney(remaining)}</p>
                  <p className="mt-1 text-xs text-muted">{view.remaining}</p>
                </div>
              </div>

              <div className="mb-1 flex items-center justify-between text-sm">
                <span className="text-muted">
                  {isForfait ? view.consumptionRateForfait : view.consumptionRate}
                </span>
                <strong className="text-foreground">{Number(consumptionPct ?? 0).toFixed(2)}%</strong>
              </div>
              <div className="mb-3 h-3 overflow-hidden rounded-full bg-card-muted">
                <div
                  className={cn(
                    "h-full rounded-full transition-all",
                    progressBarClass(consumptionPct, threshold),
                  )}
                  style={{ width: `${Math.min(Number(consumptionPct ?? 0), 100)}%` }}
                />
              </div>

              {consumptionPct >= threshold && consumptionPct < 100 ? (
                <div className="rounded-lg border border-yellow/30 bg-yellow-soft px-4 py-3 text-sm text-yellow">
                  {view.alertWarning.replace("{threshold}", String(threshold))}
                </div>
              ) : null}
              {consumptionPct >= 100 ? (
                <div className="rounded-lg border border-pink/30 bg-pink-soft px-4 py-3 text-sm text-pink">
                  {view.budgetDepleted}
                </div>
              ) : null}

              <div className="mt-6 border-t border-border pt-5">
                <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                  <h3 className="text-sm font-semibold text-foreground">{view.invoicesTitle}</h3>
                  {consumption ? (
                    <p className="text-xs text-muted">
                      {view.invoicesPeriod
                        .replace("{start}", consumption.period_start)
                        .replace("{end}", consumption.period_end)}
                      {" · "}
                      {view.invoicesCount.replace("{count}", String(consumption.invoice_count))}
                    </p>
                  ) : null}
                </div>

                {consumptionInvoices.length > 0 ? (
                  <div className="overflow-x-auto rounded-lg border border-border">
                    <table className="w-full min-w-[720px]">
                      <thead>
                        <tr className="border-b border-border bg-card-muted text-left text-[11px] font-semibold uppercase tracking-wide text-muted">
                          <th className="px-4 py-2">{view.invoiceColumns.reference}</th>
                          <th className="px-4 py-2">{view.invoiceColumns.provider}</th>
                          <th className="px-4 py-2">{view.invoiceColumns.period}</th>
                          <th className="px-4 py-2">{view.invoiceColumns.nature}</th>
                          <th className="px-4 py-2">{view.invoiceColumns.status}</th>
                          <th className="px-4 py-2 text-right">{view.invoiceColumns.amount}</th>
                          <th className="px-4 py-2 text-right">{view.invoiceColumns.actions}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {consumptionInvoices.map((invoice) => (
                          <tr key={invoice.id} className="border-b border-border last:border-0">
                            <td className="px-4 py-3 text-sm font-medium text-foreground">
                              {invoice.reference}
                            </td>
                            <td className="px-4 py-3 text-sm text-foreground">{invoice.provider}</td>
                            <td className="px-4 py-3 text-sm text-muted">{invoice.period_label}</td>
                            <td className="px-4 py-3">
                              <span
                                className={cn(
                                  "inline-flex rounded-md px-2 py-1 text-[11px] font-semibold uppercase tracking-wide",
                                  invoice.nature === "forfait_mensuel"
                                    ? "bg-primary-soft text-primary"
                                    : "bg-card-muted text-muted-foreground",
                                )}
                              >
                                {invoice.nature_label}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm text-muted">{invoice.status}</td>
                            <td className="px-4 py-3 text-right text-sm font-medium text-foreground">
                              {formatMoney(invoice.amount_usd)}
                            </td>
                            <td className="px-4 py-3 text-right">
                              <Link
                                to={`/dashboard/invoices/${invoice.id}`}
                                className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
                              >
                                {view.viewInvoice}
                                <ExternalLink size={12} />
                              </Link>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="bg-card-muted">
                          <td
                            colSpan={5}
                            className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-muted"
                          >
                            {view.invoiceColumns.total}
                          </td>
                          <td className="px-4 py-3 text-right text-sm font-bold text-foreground">
                            {formatMoney(invoicesTotal)}
                          </td>
                          <td />
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                ) : (
                  <p className="rounded-lg border border-dashed border-border px-4 py-8 text-center text-sm text-muted">
                    {view.invoicesEmpty}
                  </p>
                )}
              </div>
            </div>
          </section>
        </div>

        <div className="space-y-5">
          <section className="theme-card overflow-hidden">
            <div className="border-b border-border px-5 py-3">
              <h2 className="text-sm font-semibold text-foreground">{view.financialParams}</h2>
            </div>
            <dl className="px-5 py-2">
              <SideRow
                label={formFields.individualCeiling}
                value={formatMoney(convention.individual_ceiling)}
              />
              <SideRow
                label={formFields.monthlyPackageAmount}
                value={
                  convention.monthly_package_amount != null && convention.monthly_package_amount > 0 ? (
                    <span>
                      {formatMoney(convention.monthly_package_amount)}
                      <span className="mt-0.5 block text-xs font-normal text-muted">
                        {view.globalForfait}
                      </span>
                    </span>
                  ) : providerForfaits.length > 0 ? (
                    <span>
                      {view.providerRates.replace("{count}", String(providerForfaits.length))}
                      <span className="mt-0.5 block text-xs font-normal text-muted">
                        {formatMoney(Math.min(...providerForfaits))} –{" "}
                        {formatMoney(Math.max(...providerForfaits))} {view.perFamily}
                      </span>
                    </span>
                  ) : (
                    "—"
                  )
                }
              />
              <SideRow
                label={formFields.copayPct}
                value={`${convention.copay_pct}${labels.create.suffixes.percent}`}
              />
              <SideRow
                label={view.alertThreshold}
                value={`${convention.global_budget_alert_threshold}${labels.create.suffixes.percent}`}
              />
              {convention.budget_periodicity ? (
                <SideRow
                  label={formFields.budgetPeriodicity}
                  value={convention.budget_periodicity}
                />
              ) : null}
              {convention.coverage_unit ? (
                <SideRow
                  label={formFields.coverageUnit}
                  value={
                    labels.create.coverageUnits[
                      convention.coverage_unit as keyof typeof labels.create.coverageUnits
                    ] ?? convention.coverage_unit
                  }
                />
              ) : null}
            </dl>
          </section>

          <section className="theme-card overflow-hidden">
            <div className="border-b border-border px-5 py-3">
              <h2 className="text-sm font-semibold text-foreground">{view.beneficiaryRules}</h2>
            </div>
            <dl className="px-5 py-2">
              <SideRow
                label={view.maxChildAge}
                value={`${convention.child_age_limit} ${view.years}`}
              />
              <SideRow
                label={view.maxStudentAge}
                value={`${convention.student_child_age_limit} ${view.years}`}
              />
              <SideRow
                label={view.maxSpouseAge}
                value={
                  convention.spouse_age_limit != null
                    ? `${convention.spouse_age_limit} ${view.years}`
                    : view.noLimit
                }
              />
              <SideRow
                label={view.maxDependents}
                value={String(convention.max_dependents_per_employee)}
              />
            </dl>
            {coveredTypes.length > 0 ? (
              <div className="border-t border-border px-5 py-3">
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted">
                  {view.coveredTypes}
                </p>
                <div className="flex flex-wrap gap-2">
                  {coveredTypes.map((type) => (
                    <span
                      key={type}
                      className="inline-flex rounded-md bg-teal-soft px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-green"
                    >
                      {type}
                    </span>
                  ))}
                </div>
              </div>
            ) : null}
          </section>
        </div>
      </div>
    </>
  );
}
